import React from "react";
import { motion } from "framer-motion";

// ✅ Efficient button component with 2 variants (yellow and blue)
const Button = ({ children, href, variant = "yellow", className = "" }) => {
  const styles = {
    yellow: "bg-yellow-400 text-black hover:bg-yellow-500",
    blue: "bg-[#0000FF] text-white hover:bg-blue-600",
  };

  return (
    <a
      href={href}
      className={`inline-block text-lg px-6 py-3 rounded-2xl shadow-[0_4px_12px_rgba(0,0,0,0.4)] transition duration-200 ${styles[variant]} ${className}`}
    >
      {children}
    </a>
  );
};

export default function App() {
  return (
    <main className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen px-6 py-12">
      {/* Hero */}
      <section className="text-center max-w-4xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-5xl font-bold mb-6"
        >
          Welcome to <span className="text-yellow-400">Goldmine</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="text-lg mb-8 text-gray-300"
        >
          SEO optimized, customized website that improves your presence and generate leads
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="space-x-4"
        >
          <Button href="tel:4168561803">Call Now</Button>
          <Button href="sms:4168561803">Send SMS</Button>
          <Button href="https://wa.me/14168561803">WhatsApp</Button>
        </motion.div>
      </section>

      {/* Services */}
      <section className="mt-24 text-center max-w-5xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-3xl font-semibold mb-10"
        >
          What We Offer
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: "Web Design",
              desc: "Beautiful, responsive websites tailored for your business.",
            },
            {
              title: "SEO & Google Rankings",
              desc: "Be found on Google with optimized content and local search.",
            },
            {
              title: "Social Media Growth",
              desc: "Grow your audience with reels, posts, and automation.",
            },
          ].map((s, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.03 }}
              className="bg-gray-800 p-6 rounded-xl shadow-md"
            >
              <h3 className="text-xl font-bold text-yellow-300 mb-2">
                {s.title}
              </h3>
              <p className="text-gray-400">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Portfolio */}
      <section className="mt-24 max-w-5xl mx-auto">
        <motion.h2
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-3xl font-semibold text-center mb-10"
        >
          Portfolio Highlights
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {["PizzaPlace", "LawnCare", "BeautyBar", "Electrician", "Photographer", "Bakery"].map((site, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.03 }}
              className="bg-gray-800 rounded-xl p-4 shadow-md text-center"
            >
              <div className="text-yellow-300 font-bold text-lg mb-2">{site}</div>
              <p className="text-sm text-gray-400">Demo business website</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="mt-24 max-w-4xl mx-auto text-center">
        <motion.h2
          whileInView={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="text-3xl font-semibold mb-10"
        >
          What Clients Say
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              name: "John D.",
              review: "Goldmine made my website in a day — and I'm getting leads every week!",
            },
            {
              name: "Amira S.",
              review: "They handled everything. My Instagram is growing and my Google reviews doubled.",
            },
          ].map((t, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.02 }}
              className="bg-gray-800 p-6 rounded-xl text-left shadow"
            >
              <p className="text-gray-300 italic mb-2">“{t.review}”</p>
              <div className="text-yellow-400 font-semibold">— {t.name}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Us */}
      <section className="mt-24 text-center">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-3xl font-semibold mb-6"
        >
          Ready to Get Started?
        </motion.h2>
        <p className="text-gray-400 mb-4">
          Let’s build something amazing for your business.
        </p>
        <Button href="#contact" variant="blue">Contact Us</Button>
      </section>

      {/* Footer */}
      <footer className="mt-24 text-center text-gray-500 text-sm py-10">
        &copy; {new Date().getFullYear()} Goldmine. All rights reserved.
      </footer>
    </main>
  );
}
